// Info tab (view mode): what a visitor wants to know about a shared plan —
// name, who made it, its description, and what's in it. With a selection it
// shows the selected element(s) instead (v1 view InfoTab parity).

import { esc, cap, elementLabel, profileLink } from './html';
import { elementArea, elementLength, segmentLengths, formatLength, formatArea } from '../model/measure';

export function infoPanelHtml(state, { owner, description, collapsed } = {}) {
  const selected = state.elements.filter((el) => state.selection.includes(el.id));
  if (selected.length === 1) return elementInfoHtml(state, selected[0], collapsed);
  if (selected.length > 1) return selectionInfoHtml(state, selected);
  const plantCount = new Set(state.elements.flatMap((el) => el.speciesIds || [])).size;
  return `
    <h3 class="pl2-section-title">${esc(state.name || state.settings?.name || 'Garden plan')}</h3>
    ${owner ? `<p class="pl2-muted">by <a href="/${esc(owner)}/plans">${esc(owner)}</a></p>` : ''}
    ${description ? `<p class="pl2-info-description">${esc(description)}</p>` : ''}
    <p class="pl2-muted">${state.elements.length} element${state.elements.length === 1 ? '' : 's'} · ${plantCount} plant species</p>
    <p class="pl2-muted">Click an element on the plan to see its details. Use the Elements and Plants tabs to explore, and the Layers tab to show or hide parts of the plan.</p>`;
}

const backLink = '<button class="pl2-btn" data-pl2="clear-selection" style="margin-top:12px">← Plan info</button>';

function speciesRows(state, ids) {
  return ids.map((sid) => {
    const sp = state.species[sid];
    return `<div class="pl2-row" data-species-info="${esc(sid)}"><span class="pl2-row__name">${esc(sp?.name || `Plant #${sid}`)}</span>${profileLink(sp?.link)}</div>`;
  }).join('');
}

const field = (label, value, attrs = '') => (value
  ? `<div class="pl2-field"${attrs}><label>${label}</label><span class="pl2-field__value">${esc(value)}</span></div>`
  : '');
const section = (title, body) => (body ? `<div class="pl2-section-title" style="margin-top:12px">${title}</div>${body}` : '');

// v1 "Dimensions": area (or a line's length) first, then size / width+height
// and one row per polygon/line segment (hovering a row highlights that segment
// on the canvas). Collapsible; the open/closed state lives on the controller
// because the panel is rebuilt on every store change.
function dimensionsHtml(el, units, collapsed) {
  const d = el.data || {};
  const rows = [];
  if (el.type === 'line') { const len = elementLength(el); if (len > 0) rows.push(field('Length', formatLength(len, units))); }
  else { const a = elementArea(el); if (a > 0) rows.push(field('Area', formatArea(a, units))); }
  if (el.type === 'ellipse') rows.push(field('Size', formatLength(d.width || 0, units)));
  if (el.type === 'rectangle') rows.push(field('Width', formatLength(d.width || 0, units)), field('Height', formatLength(d.height || 0, units)));
  segmentLengths(el).forEach((len, i) => rows.push(field(`Segment ${i + 1}`, formatLength(len, units), ` data-segment="${i}" data-id="${esc(el.id)}"`)));
  if (!rows.length) return '';
  return `
    <button class="pl2-section-title pl2-collapse ${collapsed ? 'is-collapsed' : ''}" data-pl2="toggle-dimensions" aria-expanded="${!collapsed}" type="button">Dimensions</button>
    ${collapsed ? '' : rows.join('')}`;
}

function elementInfoHtml(state, el, collapsed = {}) {
  const d = el.data || {};
  const layer = d.layer ? state.layers.find((l) => l.id === d.layer) : null;
  const customLabels = (d.labels || []).filter((lb) => lb.type === 'custom' && lb.value).map((lb) => lb.value);
  return `
    <h3 class="pl2-section-title">${esc(elementLabel(el, state.species))}</h3>
    ${customLabels.length > 1 ? `<p class="pl2-muted">${customLabels.slice(1).map(esc).join(' · ')}</p>` : ''}
    ${field('Type', cap(el.type))}
    ${field('Layer', layer?.name)}
    ${field('Added', d.addedDate)}
    ${field('Removed', d.removedDate)}
    ${section('Plants', speciesRows(state, el.speciesIds || []))}
    ${section('Notes', d.notes ? `<p class="pl2-info-description">${esc(d.notes)}</p>` : '')}
    ${dimensionsHtml(el, state.settings.units, collapsed.dimensions)}
    ${backLink}`;
}

function selectionInfoHtml(state, els) {
  const species = speciesRows(state, [...new Set(els.flatMap((el) => el.speciesIds || []))]);
  const rows = els.map((el) => `<div class="pl2-row" data-pl2="element-select" data-id="${esc(el.id)}"><span class="pl2-row__name">${esc(elementLabel(el, state.species))}</span></div>`).join('');
  return `
    <h3 class="pl2-section-title">${els.length} elements selected</h3>
    ${section('Plants', species)}
    ${section('Elements', rows)}
    ${backLink}`;
}
